import React, { useState } from 'react';
import { useSolarStore } from '../../store/useSolarStore';
import { CreditCard, Calendar, ShieldCheck, ArrowRight, Zap, CheckCircle2 } from 'lucide-react';

export const FinancingCalculator: React.FC = () => {
  const { getActivePreset, setIsLeadModalOpen } = useSolarStore();
  const preset = getActivePreset();

  const [tenureMonths, setTenureMonths] = useState<number>(12); // 12, 24, or 36 months
  const [downPaymentPercent, setDownPaymentPercent] = useState<number>(20); // 20% equity down payment

  const totalPrice = preset.priceNgn;
  const downPaymentAmount = (totalPrice * downPaymentPercent) / 100;
  const financedAmount = totalPrice - downPaymentAmount;

  // Simple monthly financing calculation with standard 18% p.a. declining interest
  const annualInterestRate = 0.18;
  const monthlyInterestRate = annualInterestRate / 12;
  const monthlyRepayment = (financedAmount * monthlyInterestRate * Math.pow(1 + monthlyInterestRate, tenureMonths)) / (Math.pow(1 + monthlyInterestRate, tenureMonths) - 1);

  // Compare with monthly fuel spend (assume ~10,000/day = 300,000/mo)
  const estimatedMonthlyFuel = 300000;
  const monthlySavingsVsFuel = estimatedMonthlyFuel - Math.round(monthlyRepayment);

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-10 shadow-2xl text-white space-y-8 my-10">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 pb-6 border-b border-slate-800">
        <div>
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold uppercase tracking-wider mb-2">
            <CreditCard className="w-3.5 h-3.5" /> Solar BNPL & Lease-to-Own Financing
          </div>
          <h3 className="text-2xl sm:text-3xl font-extrabold text-white">
            Buy Now, Pay Later (BNPL) Solar Financing
          </h3>
          <p className="text-xs sm:text-sm text-slate-400 mt-1">
            Get your {preset.name} installed with as low as 20% equity down payment and spread the balance over flexible monthly installments.
          </p>
        </div>

        <div className="bg-slate-950 px-6 py-4 rounded-2xl border border-emerald-500/30 text-right">
          <span className="text-xs text-slate-400 block font-medium">Estimated Monthly Repayment</span>
          <span className="text-2xl sm:text-3xl font-black text-emerald-400">₦{Math.round(monthlyRepayment).toLocaleString()} /mo</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        {/* Left: Interactive Controls */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-slate-950 p-6 rounded-2xl border border-slate-800 space-y-5">
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-300 flex items-center justify-between">
                <span>Repayment Tenure</span>
                <span className="text-emerald-400 font-extrabold">{tenureMonths} Months</span>
              </label>
              <div className="grid grid-cols-3 gap-3">
                {[12, 24, 36].map((m) => (
                  <button
                    key={m}
                    onClick={() => setTenureMonths(m)}
                    className={`py-2.5 rounded-xl text-xs font-bold transition-all border ${
                      tenureMonths === m
                        ? 'bg-emerald-500 border-emerald-500 text-white shadow-lg'
                        : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700'
                    }`}
                  >
                    {m} Months
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-300 flex items-center justify-between">
                <span>Equity Down Payment</span>
                <span className="text-amber-400 font-extrabold">{downPaymentPercent}% (₦{Math.round(downPaymentAmount).toLocaleString()})</span>
              </label>
              <input
                type="range"
                min="20"
                max="50"
                step="5"
                value={downPaymentPercent}
                onChange={(e) => setDownPaymentPercent(Number(e.target.value))}
                className="w-full accent-emerald-500 cursor-pointer"
              />
              <div className="flex justify-between text-[10px] text-slate-500">
                <span>20% Minimum</span>
                <span>35%</span>
                <span>50%</span>
              </div>
            </div>
          </div>

          <div className="bg-emerald-950/30 p-4 rounded-2xl border border-emerald-500/30 space-y-2">
            <div className="text-xs font-bold text-emerald-300 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" /> Positive Cash Flow Guarantee
            </div>
            <p className="text-xs text-slate-300">
              Your monthly solar repayment of <strong className="text-white">₦{Math.round(monthlyRepayment).toLocaleString()}</strong> is significantly lower than your estimated monthly fuel spend of <strong className="text-rose-400">₦300,000</strong>. You start saving from Month 1!
            </p>
          </div>

          <button
            onClick={() => setIsLeadModalOpen(true, `BNPL Financing Application for ${preset.name}`)}
            className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-emerald-500/25 transition-all flex items-center justify-center gap-2 text-sm"
          >
            Apply for BNPL Solar Financing
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Right: Breakdown Table */}
        <div className="lg:col-span-7 bg-slate-950 p-6 sm:p-8 rounded-2xl border border-slate-800 space-y-6">
          <h4 className="font-bold text-white text-base flex items-center gap-2">
            <Calendar className="w-5 h-5 text-emerald-400" />
            Financing Breakdown & Disbursement Schedule
          </h4>

          <div className="space-y-4 text-xs">
            <div className="flex justify-between p-3.5 rounded-xl bg-slate-900 border border-slate-800">
              <span className="text-slate-400">Total System Outright Price:</span>
              <span className="font-bold text-white">₦{totalPrice.toLocaleString()}</span>
            </div>

            <div className="flex justify-between p-3.5 rounded-xl bg-slate-900 border border-slate-800">
              <span className="text-slate-400">Equity Down Payment ({downPaymentPercent}%):</span>
              <span className="font-bold text-amber-400">₦{Math.round(downPaymentAmount).toLocaleString()}</span>
            </div>

            <div className="flex justify-between p-3.5 rounded-xl bg-slate-900 border border-slate-800">
              <span className="text-slate-400">Financed Loan Principal:</span>
              <span className="font-bold text-cyan-400">₦{Math.round(financedAmount).toLocaleString()}</span>
            </div>

            <div className="flex justify-between p-3.5 rounded-xl bg-emerald-950/40 border border-emerald-500/40">
              <span className="text-emerald-300 font-bold">Monthly Repayment ({tenureMonths} Months):</span>
              <span className="font-black text-emerald-400 text-sm">₦{Math.round(monthlyRepayment).toLocaleString()} /mo</span>
            </div>
          </div>

          <div className="pt-2 text-[11px] text-slate-500 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
            Partnered with leading Nigerian commercial banks & fintech asset lenders. Instant approval subject to KYC verification.
          </div>
        </div>
      </div>
    </div>
  );
};
